import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { XCircle } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EntityLookup } from "@/components/shared/EntityLookup";
import { DataTable, type RowAction } from "@/components/data-table/DataTable";
import { usePagedQuery } from "@/components/data-table/usePagedQuery";
import { formatDateBr, toLocalDateTimeString } from "@/lib/format";
import { COBRANCA_PATH, getInstituicaoLookup, ignorarCobranca, type Cobranca, type CobrancaFiltro } from "./api";
import {
  SITUACAO_COBRANCA_BADGE as SITUACAO_BADGE,
  formatValorCobranca,
  formatMetodoPagamento,
  METODO_PAGAMENTO_LABEL,
} from "./format";

// Porte de app/controllers/consulta-cobranca.controller.js +
// app/templates/cobranca/consulta-cobranca.html — as 3 rotas de Cobrança
// (/Cobranca/Consulta, /Cobranca/Consulta/:filtroId,
// /Cobranca/Consulta/Instituicao/:instituicaoId) compartilham o mesmo
// template e controller no legado; aqui compartilham este mesmo componente,
// que lê os params pra decidir o filtro inicial (ver E.7 do plano).
const QUERY_KEY = ["cobranca"];

const columns: ColumnDef<Cobranca, unknown>[] = [
  {
    id: "instituicao.codigo",
    accessorFn: (row) => row.instituicao?.codigo,
    header: "Código",
    enableSorting: true,
  },
  {
    id: "instituicao.nomeFantasia",
    accessorFn: (row) => row.instituicao?.nomeFantasia,
    header: "Instituição",
    enableSorting: true,
  },
  {
    accessorKey: "dataEmissao",
    header: "Data Emissão",
    enableSorting: true,
    cell: ({ getValue }) => formatDateBr(getValue() as string | null),
  },
  {
    accessorKey: "dataVencimento",
    header: "Data Vencimento",
    enableSorting: true,
    cell: ({ getValue }) => formatDateBr(getValue() as string | null),
  },
  {
    accessorKey: "valorCobranca",
    header: "Valor",
    enableSorting: true,
    cell: ({ getValue }) => formatValorCobranca(getValue() as number),
  },
  {
    accessorKey: "situacaoCobranca",
    header: "Situação",
    enableSorting: true,
    cell: ({ getValue }) => {
      const v = String(getValue() ?? "");
      const badge = SITUACAO_BADGE[v];
      return badge ? <Badge className={badge.className}>{badge.label}</Badge> : "N/D";
    },
  },
  {
    accessorKey: "metodoPagamento",
    header: "Método de pagamento",
    enableSorting: true,
    cell: ({ getValue }) => formatMetodoPagamento(getValue() as string | null),
  },
  {
    accessorKey: "seIgnorarCobranca",
    header: "Ignorada",
    enableSorting: true,
    cell: ({ getValue }) =>
      getValue() === "S" ? (
        <Badge className="border-transparent bg-red-100 text-red-800 hover:bg-red-100">Sim</Badge>
      ) : (
        <Badge variant="outline">Não</Badge>
      ),
  },
];

// yyyy-MM-dd a partir de componentes locais — nunca via Date.toISOString(),
// que converte pra UTC e pode voltar um dia em fusos atrás de UTC (ver E.2).
function dataLocalStr(ano: number, mes: number, dia: number): string {
  return `${ano}-${String(mes + 1).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
}

function primeiroDiaMes(): string {
  const d = new Date();
  return toLocalDateTimeString(dataLocalStr(d.getFullYear(), d.getMonth(), 1));
}

function ultimoDiaMes(): string {
  const d = new Date();
  const ultimoDia = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return toLocalDateTimeString(dataLocalStr(d.getFullYear(), d.getMonth(), ultimoDia), true);
}

export default function ConsultaCobranca() {
  const params = useParams<{ filtroId?: string; instituicaoId?: string }>();
  const qc = useQueryClient();

  const instituicaoIdParam = params.instituicaoId ? Number(params.instituicaoId) : undefined;
  const filtroIdParam = params.filtroId ? Number(params.filtroId) : undefined;

  const { data: instituicaoPre, isLoading: carregandoInstituicao } = useQuery({
    queryKey: ["cobranca", "instituicao-lookup", instituicaoIdParam],
    queryFn: () => getInstituicaoLookup(instituicaoIdParam!),
    enabled: !!instituicaoIdParam,
  });

  const initialFiltro: CobrancaFiltro = useMemo(() => {
    const base: CobrancaFiltro = { seIgnoradas: "N" };
    // Porte de app/routes.js: filtroId=1 -> só vencidas; filtroId=2 -> vencidas
    // do mês corrente. Comparação com Number() em vez do `==` contra string
    // do legado — mesmo resultado, tipado.
    if (filtroIdParam === 1) {
      base.situacaoCobranca = "V";
    } else if (filtroIdParam === 2) {
      base.situacaoCobranca = "V";
      base.dataInicial = primeiroDiaMes();
      base.dataFinal = ultimoDiaMes();
    }
    if (instituicaoPre) {
      base.instituicao = instituicaoPre;
      base.instituicaoId = instituicaoPre.id;
    }
    return base;
  }, [filtroIdParam, instituicaoPre]);

  // Espera a instituição pré-selecionada carregar antes de montar a consulta
  // paginada — evita ter que sincronizar `initialFiltro` via setState num
  // efeito depois que o usePagedQuery já montou com um filtro inicial vazio.
  if (instituicaoIdParam && carregandoInstituicao) {
    return <Skeleton className="h-64 w-full" />;
  }

  return (
    <ConsultaCobrancaConteudo key={filtroIdParam ?? instituicaoIdParam ?? "default"} initialFiltro={initialFiltro} qc={qc} />
  );
}

function ConsultaCobrancaConteudo({
  initialFiltro,
  qc,
}: {
  initialFiltro: CobrancaFiltro;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const query = usePagedQuery<Cobranca, CobrancaFiltro>({
    key: QUERY_KEY,
    path: COBRANCA_PATH,
    initialFiltro,
    initialSort: { field: "dataVencimento", direction: "DESC" },
  });

  const ignorar = useMutation({
    mutationFn: ignorarCobranca,
    onSuccess: () => {
      toast.success("Cobrança ignorada.");
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const actions: RowAction<Cobranca>[] = [
    {
      label: "Ignorar",
      icon: XCircle,
      onClick: (row) => ignorar.mutate(row.id),
      confirm: { title: "Ignorar cobrança?", confirmLabel: "Ignorar" },
    },
  ];

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold text-foreground">Cobranças</h1>

      <DataTable
        query={query}
        columns={columns}
        actions={actions}
        getRowId={(r) => r.id}
        exportFilename="cobrancas"
        emptyMessage="Nenhuma cobrança encontrada."
        toolbar={
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-40">
              <Label htmlFor="f-data-inicial">Período inicial</Label>
              <Input
                id="f-data-inicial"
                type="date"
                value={query.draft.dataInicial?.slice(0, 10) ?? ""}
                onChange={(e) =>
                  query.setDraft((f) => ({
                    ...f,
                    dataInicial: e.target.value ? toLocalDateTimeString(e.target.value) : undefined,
                  }))
                }
              />
            </div>
            <div className="w-40">
              <Label htmlFor="f-data-final">Período final</Label>
              <Input
                id="f-data-final"
                type="date"
                value={query.draft.dataFinal?.slice(0, 10) ?? ""}
                onChange={(e) =>
                  query.setDraft((f) => ({
                    ...f,
                    dataFinal: e.target.value ? toLocalDateTimeString(e.target.value, true) : undefined,
                  }))
                }
              />
            </div>
            <div className="w-40">
              <Label htmlFor="f-situacao-parcela">Situação parcela</Label>
              <Select
                value={query.draft.situacaoCobranca ?? "__all__"}
                onValueChange={(v) => query.setDraft((f) => ({ ...f, situacaoCobranca: v === "__all__" ? undefined : v }))}
              >
                <SelectTrigger id="f-situacao-parcela">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">Em aberto</SelectItem>
                  <SelectItem value="V">Vencido</SelectItem>
                  <SelectItem value="P">Pago</SelectItem>
                  <SelectItem value="__all__">Todas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label htmlFor="f-situacao-instituicao">Situação instituição</Label>
              <Select
                value={query.draft.situacaoInstituicao ?? "__all__"}
                onValueChange={(v) => query.setDraft((f) => ({ ...f, situacaoInstituicao: v === "__all__" ? undefined : v }))}
              >
                <SelectTrigger id="f-situacao-instituicao">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">Ativo</SelectItem>
                  <SelectItem value="T">Trial</SelectItem>
                  <SelectItem value="I">Inativo</SelectItem>
                  <SelectItem value="C">Cancelado</SelectItem>
                  <SelectItem value="__all__">Todas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label htmlFor="f-metodo-pagamento">Método de pagamento</Label>
              <Select
                value={query.draft.metodoPagamento ?? "__all__"}
                onValueChange={(v) => query.setDraft((f) => ({ ...f, metodoPagamento: v === "__all__" ? undefined : v }))}
              >
                <SelectTrigger id="f-metodo-pagamento">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  {Object.entries(METODO_PAGAMENTO_LABEL).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label htmlFor="f-ignoradas">Mostrar ignoradas</Label>
              <Select
                value={query.draft.seIgnoradas}
                onValueChange={(v) => query.setDraft((f) => ({ ...f, seIgnoradas: v as "S" | "N" }))}
              >
                <SelectTrigger id="f-ignoradas">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="S">Sim</SelectItem>
                  <SelectItem value="N">Não</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-64">
              <EntityLookup
                label="Instituição"
                entityPath="instituicao/Instituicao"
                searchByCodeAndName
                displayCode
                value={query.draft.instituicao ?? null}
                onChange={(v) =>
                  query.setDraft((f) => ({ ...f, instituicao: v ?? undefined, instituicaoId: v?.id }))
                }
              />
            </div>
            <Button onClick={query.search}>Buscar</Button>
          </div>
        }
      />
    </div>
  );
}
